import { useState, useEffect, useRef, useCallback } from "react";

interface PwaStatus {
  isStandalone: boolean;
  updateWaiting: boolean;
  applyUpdate: () => void;
}

export function usePwaStatus(): PwaStatus {
  const [isStandalone, setIsStandalone] = useState(
    () => window.matchMedia('(display-mode: standalone)').matches ||
          Reflect.get(navigator, 'standalone') === true,
  );
  const [updateWaiting, setUpdateWaiting] = useState(false);
  const waitingSwRef = useRef<ServiceWorker | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    const handler = (e: MediaQueryListEvent) => setIsStandalone(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let stopped = false;

    const markWaiting = (sw: ServiceWorker) => {
      if (navigator.serviceWorker.controller == null) return;
      waitingSwRef.current = sw;
      setUpdateWaiting(true);
    };

    navigator.serviceWorker.getRegistration().then(reg => {
      if (stopped || reg == null) return;
      if (reg.waiting != null) markWaiting(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (installing == null) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed') markWaiting(installing);
        });
      });
    }).catch(() => { /* SW not supported or blocked */ });

    // On first visit the SW's clients.claim() fires controllerchange too;
    // reloading then would wipe page state (e.g. half-filled forms) at an
    // unpredictable moment. Only reload when an existing controller is
    // replaced, i.e. an update actually took over.
    let hadController = navigator.serviceWorker.controller != null;
    const onControllerChange = () => {
      if (stopped) return;
      if (!hadController) { hadController = true; return; }
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => {
      stopped = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    waitingSwRef.current?.postMessage({ type: 'skip-waiting' });
  }, []);

  return { isStandalone, updateWaiting, applyUpdate };
}
